import React, { useEffect, useMemo, useState } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import Seo from '@/components/Seo';

const buildPricingData = () => ({
  UK: {
    label: 'United Kingdom',
    currencySymbol: '£',
    taxLabel: 'VAT',
    plans: [
      { id: 'starter', name: 'Starter', price: '59' },
      { id: 'growth', name: 'Growth', price: '99' },
      { id: 'pro', name: 'Pro', price: '149' }
    ],
    categories: [
      {
        title: 'Review Management',
        items: [
          { label: 'Review Response', values: { starter: true, growth: true, pro: true } },
          { label: 'Fake Review Reporting', values: { starter: true, growth: true, pro: true } },
          { label: 'Monthly Review Report', values: { starter: true, growth: true, pro: true } }
        ]
      },
      {
        title: 'Revenue Guard',
        items: [
          { label: 'Monitor Profile Risk & Health Check', values: { starter: false, growth: true, pro: true } },
          { label: 'Detect Abnormal Drops', values: { starter: false, growth: true, pro: true } },
          { label: 'Maintain reinstatement assets', values: { starter: false, growth: true, pro: true } },
          { label: 'Handle Escalation Prep', values: { starter: false, growth: true, pro: true } },
          { label: 'Monthly Insights Report', values: { starter: false, growth: true, pro: true } }
        ]
      },
      {
        title: 'Local SEO',
        items: [
          { label: 'Google Posts + Offers', values: { starter: false, growth: 'up to 4 Post / month', pro: 'up to 8 Post / month' } },
          { label: 'Keyword Research', values: { starter: false, growth: true, pro: true } },
          { label: 'Performance Optimisation', values: { starter: false, growth: true, pro: true } },
          { label: 'Competitor Analysis', values: { starter: false, growth: false, pro: true } },
          { label: 'Monthly Local SEO Report', values: { starter: false, growth: false, pro: true } }
        ]
      }
    ],
    addons: [
      {
        title: 'GBP Audit + Profile Optimisation',
        description: 'Title, Description, Categories, Contact Details, Local Keywords, Social Profile links, Product, Services, Photos',
        prices: {
          starter: '£99/One-off',
          growth: '£99/One-off',
          pro: 'Including (Recurring)'
        }
      },
      {
        title: 'Single Page Website',
        description: 'A very basic website built to support the SEO work',
        prices: {
          starter: '£99 setup + £19 / month',
          growth: '£99 setup + £19 / month',
          pro: 'Including (Recurring)'
        }
      },
      {
        title: 'DMCC Fake Review Compliance Audit & Risk Check + Guidance',
        description: 'UK-only review compliance assessment',
        prices: {
          starter: '£149/One-off',
          growth: '£149/One-off',
          pro: '£149/One-off'
        }
      },
      {
        title: 'Review Cleanup:',
        description: 'Reply to negative review from the last 12 months',
        prices: {
          starter: '£99/One-off',
          growth: '£99/One-off',
          pro: '£99/One-off'
        }
      }
    ]
  },
  IN: {
    label: 'India',
    currencySymbol: '₹',
    taxLabel: 'GST',
    plans: [
      { id: 'starter', name: 'Starter', price: '2,999' },
      { id: 'growth', name: 'Growth', price: '5,999' },
      { id: 'pro', name: 'Pro', price: '9,999' }
    ],
    categories: [
      {
        title: 'Review Management',
        items: [
          { label: 'Review Response', values: { starter: true, growth: true, pro: true } },
          { label: 'Fake Review Reporting', values: { starter: true, growth: true, pro: true } },
          { label: 'Monthly Review Report', values: { starter: true, growth: true, pro: true } }
        ]
      },
      {
        title: 'Revenue Guard',
        items: [
          { label: 'Monitor Profile Risk & Health Check', values: { starter: false, growth: true, pro: true } },
          { label: 'Detect Abnormal Drops', values: { starter: false, growth: true, pro: true } },
          { label: 'Maintain reinstatement assets', values: { starter: false, growth: true, pro: true } },
          { label: 'Handle Escalation Prep', values: { starter: false, growth: true, pro: true } },
          { label: 'Monthly Insights Report', values: { starter: false, growth: true, pro: true } }
        ]
      },
      {
        title: 'Local SEO',
        items: [
          { label: 'Google Posts + Offers', values: { starter: false, growth: 'up to 4 Post / month', pro: 'up to 8 Post / month' } },
          { label: 'Keyword Research', values: { starter: false, growth: true, pro: true } },
          { label: 'Performance Optimisation', values: { starter: false, growth: true, pro: true } },
          { label: 'Competitor Analysis', values: { starter: false, growth: false, pro: true } },
          { label: 'Monthly Local SEO Report', values: { starter: false, growth: false, pro: true } }
        ]
      }
    ],
    addons: [
      {
        title: 'GBP Audit + Profile Optimisation',
        description: 'Title, Description, Categories, Contact Details, Local Keywords, Social Profile links, Product, Services, Photos',
        prices: {
          starter: '₹5,999/One-off',
          growth: '₹5,999/One-off',
          pro: 'Including (Recurring)'
        }
      },
      {
        title: 'Single Page Website',
        description: 'A very basic website built to support the SEO work',
        prices: {
          starter: '₹5,999 one off + ₹999 / month',
          growth: '₹5,999 one off + ₹999 / month',
          pro: 'Including (Recurring)'
        }
      },
      {
        title: 'Review Cleanup:',
        description: 'Reply to negative review from the last 12 months',
        prices: {
          starter: '₹9,999/One-off',
          growth: '₹9,999/One-off',
          pro: '₹9,999/One-off'
        }
      }
    ]
  }
});

const renderCellValue = (value) => {
  if (value === true) return <span className="text-slate-900">✓</span>;
  if (value === false) return <span className="text-slate-300">✕</span>;
  return value;
};

export default function PricingPage() {
  const pricingData = useMemo(() => buildPricingData(), []);
  const [region, setRegion] = useState('UK');
  const data = pricingData[region];

  useEffect(() => {
    const controller = new AbortController();

    const loadRegion = async () => {
      try {
        const response = await fetch('/cdn-cgi/trace', {
          signal: controller.signal,
          cache: 'no-store',
        });
        const text = await response.text();
        const match = text.match(/^loc=([A-Z]{2})$/m);
        const country = match ? match[1] : null;

        if (country === 'IN') {
          setRegion('IN');
        } else {
          setRegion('UK');
        }
      } catch (error) {
        setRegion('UK');
      }
    };

    loadRegion();

    return () => controller.abort();
  }, []);

  return (
    <Layout>
      <Seo
        title="Pricing"
        description="Compare Starter, Growth, and Pro plans for review management, revenue guard, and local SEO."
        path="/pricing"
      />
      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="text-4xl lg:text-5xl font-black font-heading text-slate-900">
                Pricing
              </h1>
              <p className="text-lg text-slate-600 mt-4 max-w-2xl font-body">
                Simple pricing for review management, revenue guard, and local SEO.
              </p>
            </div>
            <Button asChild className="bg-slate-900 text-white hover:bg-slate-800">
              <a href="/contact">Contact Us</a>
            </Button>
          </div>

          <div className="mt-10 hidden lg:block overflow-x-auto">
            <div className="min-w-[900px] rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <table className="w-full border-collapse text-sm">
                <thead className="bg-slate-50 text-slate-900">
                <tr>
                  <th className="w-[45%] border border-slate-200 p-4 text-left font-semibold"> </th>
                  {data.plans.map((plan) => (
                    <th key={plan.id} className="border border-slate-200 p-4 text-center text-lg font-bold">
                      {plan.name}
                    </th>
                  ))}
                </tr>
                <tr>
                  <th className="border border-slate-200 p-4 text-left font-semibold"> </th>
                  {data.plans.map((plan) => (
                    <th key={plan.id} className="border border-slate-200 p-4 text-center font-semibold">
                      {data.currencySymbol}{plan.price} + {data.taxLabel} / monthly
                    </th>
                  ))}
                </tr>
                </thead>
                <tbody>
                  {data.categories.map((category) => (
                    <React.Fragment key={category.title}>
                      <tr className="bg-slate-50">
                      <td colSpan={4} className="border border-slate-200 p-3 font-black text-slate-900">
                        {category.title}
                      </td>
                      </tr>
                      {category.items.map((item, index) => (
                        <tr key={item.label} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}>
                          <td className="border border-slate-200 p-3 text-slate-700">{item.label}</td>
                          {data.plans.map((plan) => (
                            <td key={plan.id} className="border border-slate-200 p-3 text-center text-slate-700">
                              {renderCellValue(item.values[plan.id])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  <tr className="bg-slate-50">
                    <td colSpan={4} className="border border-slate-200 p-3 font-black text-slate-900">
                      Add-ons
                    </td>
                  </tr>
                  {data.addons.map((addon, index) => (
                    <tr key={addon.title} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}>
                      <td className="border border-slate-200 p-3 text-slate-700">
                        <div className="font-semibold text-slate-900">{addon.title}</div>
                        <div className="text-xs text-slate-500">{addon.description}</div>
                      </td>
                      {data.plans.map((plan) => (
                        <td key={plan.id} className="border border-slate-200 p-3 text-center text-slate-700">
                          {addon.prices[plan.id]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-10 lg:hidden space-y-6">
            {data.plans.map((plan) => (
              <div key={plan.id} className="border border-slate-200 rounded-2xl p-5">
                <div className="space-y-2">
                  <span className="text-xl font-extrabold uppercase tracking-wide text-slate-700">{plan.name}</span>
                  <span className="block text-sm font-semibold text-slate-900">
                    {data.currencySymbol}{plan.price} + {data.taxLabel} / monthly
                  </span>
                </div>
                <div className="mt-4 space-y-4">
                  {data.categories.map((category) => (
                    <div key={category.title} className="space-y-3">
                      <div className="text-xs font-black uppercase tracking-wide text-slate-500">
                        {category.title}
                      </div>
                      {category.items.map((item, index) => (
                        <div
                          key={item.label}
                          className={`flex items-start justify-between gap-4 text-sm text-slate-700 pb-3 ${index < category.items.length - 1 ? 'border-b border-slate-200' : ''}`}
                        >
                          <span className="pr-3">{item.label}</span>
                          <span className="font-semibold whitespace-nowrap">{renderCellValue(item.values[plan.id])}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                  <div className="space-y-3">
                    <div className="text-xs font-black uppercase tracking-wide text-slate-500">
                      Add-ons
                    </div>
                    {data.addons.map((addon, index) => (
                      <div
                        key={addon.title}
                        className={`space-y-1 text-sm text-slate-700 pb-4 ${index < data.addons.length - 1 ? 'border-b border-slate-200' : ''}`}
                      >
                        <div className="font-semibold">{addon.title}</div>
                        <div className="text-xs text-slate-500 leading-relaxed">{addon.description}</div>
                        <div className="text-sm font-semibold text-slate-800">{addon.prices[plan.id]}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
